import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import MainNavigation from '../../components/ui/MainNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import DateSummary from './components/DateSummary';
import PricingDetails from './components/PricingDetails';
import BillingInfoForm from './components/BillingInfoForm';
import StripePaymentForm from './components/StripePaymentForm';
import TermsAcceptance from './components/TermsAcceptance';
import { fetchTrailerListingById } from '../../services/trailerListingService';
import ErrorFallback from '../../components/ui/ErrorFallback';

const stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [legalAcknowledgments, setLegalAcknowledgments] = useState({
    acknowledgeMarketplaceOnly: false,
    acknowledgeNoInsurance: false,
    agreeRentalAgreement: false,
    agreeArbitrationWaiveClassAction: false
  });
  const [trailerData, setTrailerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const bookingInfo = location?.state?.bookingData;

  useEffect(() => {
    if (!bookingInfo) {
      navigate('/search-results');
    }
  }, [bookingInfo, navigate]);

  useEffect(() => {
    const fetchTrailer = async () => {
      if (!bookingInfo?.trailerId) return;

      setLoading(true);
      setError(null);
      try {
        const data = await fetchTrailerListingById(bookingInfo?.trailerId);
        if (data) {
          setTrailerData({
            id: data?.id,
            title: data?.title,
            type: data?.trailerType,
            location: `${data?.locationCity}, ${data?.locationState}`,
            image: data?.photos?.[0] || 'https://img.rocket.new/generatedImages/rocket_gen_img_1e66491e3-1767380697290.png',
            imageAlt: `${data?.title} - ${data?.trailerType} trailer`,
            pricing: {
              dailyRate: parseFloat(data?.dailyRate || 0),
              weeklyDiscount: data?.weeklyRate
                ? Math.round((1 - (parseFloat(data?.weeklyRate) / (parseFloat(data?.dailyRate) * 7))) * 100)
                : 0,
              securityDeposit: parseFloat(data?.securityDeposit || 0),
              cleaningFee: parseFloat(data?.cleaningFee || 0)
            },
            specifications: {
              length: data?.lengthFeet ? `${data?.lengthFeet} feet` : null,
              width: data?.widthFeet ? `${data?.widthFeet} feet` : null,
              height: data?.heightFeet ? `${data?.heightFeet} feet` : null,
              weightCapacity: data?.weightCapacityLbs ? `${data?.weightCapacityLbs?.toLocaleString()} lbs` : null,
              features: Array.isArray(data?.features) ? data?.features : []
            },
            ownerId: data?.ownerId
          });
        }
      } catch (err) {
        console.error('Error fetching trailer:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTrailer();
  }, [bookingInfo?.trailerId]);

  const calculatePricing = () => {
    if (!bookingInfo?.startDate || !bookingInfo?.endDate || !trailerData) return null;

    const start = new Date(bookingInfo.startDate);
    const end = new Date(bookingInfo.endDate);
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;

    let subtotal = days * trailerData?.pricing?.dailyRate;
    let discount = 0;

    if (days >= 7) {
      discount = subtotal * (trailerData?.pricing?.weeklyDiscount / 100);
    }

    const taxRate = 0.0825;
    const tax = (subtotal - discount + trailerData?.pricing?.cleaningFee) * taxRate;
    const total = subtotal - discount + trailerData?.pricing?.securityDeposit + trailerData?.pricing?.cleaningFee + tax;

    return {
      days,
      subtotal,
      discount,
      tax,
      total,
      dailyRate: trailerData?.pricing?.dailyRate,
      weeklyDiscount: trailerData?.pricing?.weeklyDiscount,
      securityDeposit: trailerData?.pricing?.securityDeposit,
      cleaningFee: trailerData?.pricing?.cleaningFee
    };
  };

  const pricingDetails = calculatePricing();

  const handleCreatePaymentIntent = (secret, intentId, custId) => {
    setClientSecret(secret);
    setPaymentIntentId(intentId);
    setCustomerId(custId);
  };

  const handlePaymentSuccess = (paymentIntent, bookingResult) => {
    navigate('/booking-confirmation', {
      state: {
        bookingData: {
          bookingId: bookingResult?.booking?.id,
          bookingReference: bookingResult?.booking?.booking_reference || bookingResult?.bookingReference,
          status: 'confirmed',
          createdAt: new Date()?.toISOString(),
          startDate: bookingInfo?.startDate,
          endDate: bookingInfo?.endDate,
          trailer: {
            id: trailerData?.id,
            title: trailerData?.title,
            type: trailerData?.type,
            location: trailerData?.location,
            image: trailerData?.image,
            imageAlt: trailerData?.imageAlt,
            specifications: {
              length: trailerData?.specifications?.length || null,
              width: trailerData?.specifications?.width || null,
              height: trailerData?.specifications?.height || null,
              capacity: trailerData?.specifications?.weightCapacity || null,
              features: trailerData?.specifications?.features || []
            }
          },
          pricing: {
            days: bookingResult?.days,
            dailyRate: trailerData?.pricing?.dailyRate,
            subtotal: bookingResult?.subtotal,
            weeklyDiscount: bookingResult?.discount,
            discountPercent: trailerData?.pricing?.weeklyDiscount,
            securityDeposit: bookingResult?.securityDeposit,
            cleaningFee: bookingResult?.cleaningFee,
            taxRate: 8.25,
            tax: bookingResult?.taxAmount,
            total: bookingResult?.total
          },
          owner: {
            id: trailerData?.ownerId,
            name: bookingResult?.ownerProfile?.full_name || 'Owner',
            avatar: bookingResult?.ownerProfile?.profile_photo_url || 'https://img.rocket.new/generatedImages/rocket_gen_img_1fc38413b-1763294138349.png',
            avatarAlt: `${bookingResult?.ownerProfile?.full_name || 'Owner'} profile photo`,
            verified: bookingResult?.ownerProfile?.verified_status || false,
            rating: 0,
            reviewCount: 0,
            phone: bookingResult?.ownerProfile?.phone || '',
            email: bookingResult?.ownerProfile?.email || '',
            responseTime: '2 hours',
            location: [bookingResult?.ownerProfile?.city, bookingResult?.ownerProfile?.state]?.filter(Boolean)?.join(', ') || ''
          },
          pickup: {
            address: trailerData?.location || 'Contact owner for pickup address',
            instructions: 'Contact the owner to confirm exact pickup location and time.',
            accessCode: null,
            contactTime: bookingInfo?.pickupTime
              ? `Pick-up: ${bookingInfo?.pickupTime}${bookingInfo?.dropoffTime ? ` · Drop-off: ${bookingInfo?.dropoffTime}` : ''}`
              : '8:00 AM - 6:00 PM',
            pickupTime: bookingInfo?.pickupTime || null,
            dropoffTime: bookingInfo?.dropoffTime || null
          },
          payment: {
            method: 'Credit Card',
            transactionId: paymentIntent?.id,
            paidAt: new Date()?.toISOString()
          }
        },
        trailerData,
        paymentIntent
      }
    });
  };

  const handleTermsChange = (accepted) => {
    setTermsAccepted(accepted);
  };

  const handleLegalAcknowledgmentChange = (acknowledgments) => {
    setLegalAcknowledgments(acknowledgments);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <MainNavigation />
        <div className="pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="space-y-6 animate-pulse">
              <div className="h-8 bg-muted rounded w-1/4" />
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                  <div className="h-64 bg-muted rounded" />
                  <div className="h-96 bg-muted rounded" />
                </div>
                <div className="space-y-6">
                  <div className="h-48 bg-muted rounded" />
                  <div className="h-64 bg-muted rounded" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <MainNavigation />
        <div className="pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <ErrorFallback
              error={error}
              onRetry={() => window.location?.reload()}
              title="Failed to load checkout data"
            />
          </div>
        </div>
      </div>
    );
  }

  if (!trailerData || !pricingDetails) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />

      <div className="container mx-auto px-4 py-6">
        <BreadcrumbNavigation
          items={[
            { label: 'Home', path: '/' },
            { label: 'Search Results', path: '/search-results' },
            { label: trailerData?.title, path: `/trailer-detail/${trailerData?.id}` },
            { label: 'Checkout', path: '/checkout' }
          ]}
        />
      </div>

      <div className="container mx-auto px-4 py-8">
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-8">
          Complete Your Booking
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <DateSummary
              startDate={bookingInfo?.startDate}
              endDate={bookingInfo?.endDate}
              trailerData={trailerData}
              trailer={trailerData}
              days={pricingDetails?.days}
              pickupTime={bookingInfo?.pickupTime}
              dropoffTime={bookingInfo?.dropoffTime}
            />

            <TermsAcceptance
              accepted={termsAccepted}
              onChange={handleTermsChange}
              legalAcknowledgments={legalAcknowledgments}
              onLegalChange={handleLegalAcknowledgmentChange}
              onTermsChange={handleTermsChange}
              onLegalAcknowledgmentChange={handleLegalAcknowledgmentChange}
            />

            {!clientSecret ? (
              <BillingInfoForm
                onCreatePaymentIntent={handleCreatePaymentIntent}
                amount={pricingDetails?.total}
                termsAccepted={termsAccepted}
                bookingInfo={bookingInfo}
                trailerData={trailerData}
              />
            ) : (
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret,
                  appearance: {
                    theme: 'stripe',
                    variables: {
                      colorPrimary: '#f97316'
                    }
                  }
                }}
              >
                <StripePaymentForm
                  onSuccess={handlePaymentSuccess}
                  amount={pricingDetails?.total}
                  termsAccepted={termsAccepted}
                  bookingInfo={bookingInfo}
                  trailerData={trailerData}
                  legalAcknowledgments={legalAcknowledgments}
                />
              </Elements>
            )}
          </div>

          <div>
            <PricingDetails pricing={pricingDetails} trailerData={trailerData} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;